import React, { useMemo } from 'react';
import { Disclosure } from '@headlessui/react';
import { getNextSteps } from 'utils/ShipmentWebAPIUtils';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import AppointmentWindowNextSteps from 'components/modules/shipments/next-steps/AppointmentWindowNextStep';
import AssignCarrierNextStep from 'components/modules/shipments/next-steps/AssignCarrierNextStep';
import StopCompleteNextStep from 'components/modules/shipments/next-steps/CompleteNextStep';
import DateNextSteps from 'components/modules/shipments/next-steps/DateNextSteps';
import ReferenceNumbersNextStep from 'components/modules/shipments/next-steps/ReferenceNumberNextStep';
import StopArrivalNextStep from 'components/modules/shipments/next-steps/StopArrivalNextSteps';
import NonActionableNextStep from 'components/modules/shipments/next-steps/NonActionableNextStep';
import AddAccessorialNextStep from 'components/modules/shipments/next-steps/AddAccessorialNextStep';
import ApproveAccessorialNextStep from 'components/modules/shipments/next-steps/ApproveAccessorialNextStep';
import { useQuery } from '@tanstack/react-query';
const STEPS = {
    DATE: 'date',
    ASSIGN_CARRIER: 'assign_carrier',
    APPOINTMENT_WINDOW: 'appointment_window',
    COMPLETE: 'complete',
    REFERENCE_NUMBERS: 'reference_numbers',
    ARRIVAL: 'arrival',
    ADD_ACCESSORIAL: 'add_accessorial',
    APPROVE_ACCESSORIAL: 'approve_accessorial',
    NON_ACTIONABLE: 'non_actionable',
};
function filterNextStepsByUserType(mapped, isCarrier) {
    if (isCarrier) {
        return mapped.filter((step) => {
            var _a, _b;
            return !(Array.isArray((_a = step === null || step === void 0 ? void 0 : step.meta) === null || _a === void 0 ? void 0 : _a.userType) &&
                ((_b = step === null || step === void 0 ? void 0 : step.meta) === null || _b === void 0 ? void 0 : _b.userType.includes('admin')));
        });
    }
    return mapped;
}
export default function NextSteps({ shipment }) {
    var _a, _b;
    const { t } = useTranslation();
    const shipmentId = useMemo(() => shipment === null || shipment === void 0 ? void 0 : shipment._id, [shipment]);
    const { data } = useQuery({
        queryKey: ['next-actions', shipmentId],
        queryFn: async () => {
            const { entity: { results: { upcoming, complete }, }, } = await getNextSteps(shipmentId);
            return { upcoming, complete };
        },
        enabled: !!shipmentId,
    });
    const mapAdditionalStepAttributes = (steps) => {
        return (steps || []).map((step, stepIndex) => (Object.assign(Object.assign({}, step), { stepIndex, disabled: shouldDisableStopCompletion(steps, Object.assign(Object.assign({}, step), { stepIndex })) })));
    };
    const upcoming = (_a = data === null || data === void 0 ? void 0 : data.upcoming) !== null && _a !== void 0 ? _a : [];
    const complete = (_b = data === null || data === void 0 ? void 0 : data.complete) !== null && _b !== void 0 ? _b : [];
    const isCarrier = SessionStore.isCarrier;
    const nextSteps = useMemo(() => {
        const mapped = mapAdditionalStepAttributes(upcoming);
        return filterNextStepsByUserType(mapped, isCarrier);
    }, [upcoming, isCarrier]);
    const completedSteps = useMemo(() => {
        const mapped = mapAdditionalStepAttributes(complete);
        return filterNextStepsByUserType(mapped, isCarrier);
    }, [complete, isCarrier]);
    const renderViewMore = (open) => {
        const text = open ? 'HIDE' : 'VIEW_ALL';
        return (React.createElement("span", { "data-testid": "span-156de9d6", className: "text-xs text-gray-700" }, t(text)));
    };
    const renderStep = (step) => {
        const isAdmin = SessionStore.isAdmin();
        switch (step.type) {
            case STEPS.DATE:
                return React.createElement(DateNextSteps, { item: step, shipment: shipment });
            case STEPS.ASSIGN_CARRIER:
                return React.createElement(AssignCarrierNextStep, { item: step, shipment: shipment });
            case STEPS.APPOINTMENT_WINDOW:
                return React.createElement(AppointmentWindowNextSteps, { item: step, shipment: shipment });
            case STEPS.COMPLETE:
                return (React.createElement(StopCompleteNextStep, { item: step, stops: shipment.stops, shipment: shipment }));
            case STEPS.REFERENCE_NUMBERS:
                return (isAdmin && (React.createElement(ReferenceNumbersNextStep, { item: step, shipment: shipment })));
            case STEPS.ARRIVAL:
                return React.createElement(StopArrivalNextStep, { item: step, shipment: shipment });
            case STEPS.ADD_ACCESSORIAL:
                return (isAdmin && React.createElement(AddAccessorialNextStep, { item: step, shipment: shipment }));
            case STEPS.APPROVE_ACCESSORIAL:
                return (isAdmin && (React.createElement(ApproveAccessorialNextStep, { item: step, shipment: shipment })));
            case STEPS.NON_ACTIONABLE:
                return React.createElement(NonActionableNextStep, { item: step });
            default:
                return null;
        }
    };
    function shouldDisableStopCompletion(nextSteps, currentStep) {
        // Prevent completion if:
        // --there are unconfirmed appointments (except empty)
        // --there are arrivals required
        // --prior stops haven't been completed
        const { type, field, sequence, complete } = currentStep;
        if (complete) {
            return true;
        }
        if (!(['arrival', 'complete'].includes(type) ||
            (type === 'appointment_window' && field === 'appointment_unconfirmed'))) {
            return false;
        }
        const relevantSteps = nextSteps.filter((step) => (step === null || step === void 0 ? void 0 : step.stepIndex) !== (currentStep === null || currentStep === void 0 ? void 0 : currentStep.stepIndex));
        if (!relevantSteps.length) {
            return false;
        }
        // Unconfirmed Appointments
        const unconfirmedAppointments = relevantSteps.filter((step) => step.type === 'appointment_window' &&
            step.field === 'appointment_unconfirmed' &&
            step.sequence < sequence);
        // Stop Arrivals
        const incompletePriorArrivals = relevantSteps.filter((step) => step.type === 'arrival' && step.sequence < sequence);
        // Prior Stop Completions
        const incompletePriorStopCompletions = relevantSteps.filter((step) => step.type === 'complete' && step.sequence < sequence);
        const relevantStop = shipment === null || shipment === void 0 ? void 0 : shipment.stops.find((s) => { var _a; return (s === null || s === void 0 ? void 0 : s._id) === ((_a = currentStep === null || currentStep === void 0 ? void 0 : currentStep.meta) === null || _a === void 0 ? void 0 : _a.stop); });
        if (relevantStop.empty) {
            return !!(incompletePriorStopCompletions.length || incompletePriorArrivals.length);
        }
        return !!(unconfirmedAppointments.length ||
            incompletePriorArrivals.length ||
            incompletePriorStopCompletions.length);
    }
    const renderCompletedActions = () => {
        if (completedSteps.length === 0) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-8abfee1c" },
            React.createElement("p", { "data-testid": "p-d84627f3", className: "text-sm mb-2 text-gray-700 font-medium" }, t('COMPLETED_ACTIONS')),
            React.createElement("div", { "data-testid": "div-cb4b48b1" }, completedSteps.map(renderStep))));
    };
    const renderUpcomingActions = () => {
        return (React.createElement("div", { "data-testid": "div-b089e1d3" },
            React.createElement("p", { "data-testid": "p-cfc1f0c1", className: "text-sm mb-2 text-gray-700 font-medium" }, t('UPCOMING_ACTIONS')),
            nextSteps.map(renderStep)));
    };
    const renderContent = () => {
        return (React.createElement("div", { "data-testid": "div-5f0f4a46", className: "space-y-4" },
            renderUpcomingActions(),
            React.createElement("hr", { "data-testid": "hr-2058ba4c", className: "" }),
            renderCompletedActions()));
    };
    const renderNextActions = (open) => {
        if (open) {
            return null;
        }
        return (React.createElement("div", { "data-testid": "div-02289f44" }, nextSteps.slice(0, 3).map(renderStep)));
    };
    return (React.createElement(Disclosure, null, ({ open }) => (React.createElement("div", { "data-testid": "div-53844f82", className: "lg:col-span-4 mb-4" },
        React.createElement("h3", { "data-testid": "h3-1cc5f47d", className: "my-0 mb-4" }, t('NEXT_STEPS')),
        renderNextActions(open),
        !open && (React.createElement("div", { "data-testid": "div-f8cd9d99", className: "flex justify-end mt-2" },
            React.createElement(Disclosure.Button, { "data-testid": "undefined-d201503d" }, renderViewMore(open)))),
        React.createElement(Disclosure.Panel, { "data-testid": "undefined-8ca08c3b" }, renderContent()),
        open && (React.createElement("div", { "data-testid": "div-e7bbbecb", className: "flex justify-end mt-2" },
            React.createElement(Disclosure.Button, { "data-testid": "undefined-6ee47da3" }, renderViewMore(open))))))));
}
